import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { Auth0Service, IamService, LOGIN_SERVICE, LoginService } from '@netfoundry-ui/shared/authorization';
import { Environment, ENVIRONMENT } from '@netfoundry-ui/shared/model';
import {BrandingModeService, LoggerService} from '@netfoundry-ui/shared/services';
import moment from 'moment';
import { lastValueFrom } from 'rxjs';
import { ConfirmComponent } from "@netfoundry-ui/ui/confirm";

@Component({
    selector: 'app-invitation',
    templateUrl: './invitation.component.html',
    styleUrls: ['./invitation.component.scss'],
    standalone: false
})
export class InvitationComponent implements OnInit {
    environmentLogoClass = this.environment['logoClass'] != null ? this.environment['logoClass'] : '';

    screen;
    key;
    tenantLabel;
    expires;

    // Set when returning here after a failed acceptance attempt (e.g. signed in with the wrong
    // account). The invitation is still valid; we surface a notice on the review screen (MOP-27516).
    showAcceptError = false;

    provider;

    dialogRef;

    // invitation model
    invitation = {
        fromIdentity: {
            firstName: '',
            lastName: '',
            email: '',
        },
        invitedEmailAddress: '',
        toTenantName: '',
        expiration: {
            nano: 0,
            epochSecond: 0,
        },
        accepted: null,
    };

    constructor(
        private logger: LoggerService,
        private iamService: IamService,
        private activatedRoute: ActivatedRoute,
        private auth0Service: Auth0Service,
        private brandingService: BrandingModeService,
        @Inject(LOGIN_SERVICE) private auth: LoginService,
        public dialogForm: MatDialog,
        @Inject(ENVIRONMENT) public environment: Environment
    ) {
        // clear out any stray logins
        this.auth.clearStorage();
    }

    ngOnInit() {
        // getting the path params from the current route
        this.activatedRoute.queryParams.subscribe((params: Params) => {
            this.showAcceptError = !!params['acceptError'];
            if (params['invitationKey']) {
                this.key = params['invitationKey'];
                this.reviewInvitation();
            } else if (params['acceptToken'] && params['state']) {
                this.confirmInvitation(params['acceptToken'], params['state']);
            } else {
                this.screen = 'empty';
            }
        });
    }

    async accept() {
        this.logger.info('Initiate accepting invite...');

        // Get the org nfToken for the Post Login Action.
        // accept-initiate is called post-login (in appState$ subscription) so that the
        // authenticated user's identity can be embedded in the invitation token.
        let result: any;
        try {
            result = await lastValueFrom(this.iamService.authorizeInitiate(this.tenantLabel));
        } catch (err) {
            this.logger.error('authorizeInitiate failed', err);
            this.screen = 'invalid';
            return;
        }

        const nfToken = result?.nfToken;
        if (!nfToken) {
            this.logger.error('No nfToken returned from authorizeInitiate', result);
            this.screen = 'invalid';
            return;
        }

        const connectionIds: string[] = result?.auth0ConnectionIds || [];
        const hasUsernamePassword = connectionIds.includes('Username-Password-Authentication');

        const useAcul = (this.environment.authconfig as { newUniversalLogin?: boolean }).newUniversalLogin;

        const authorizationParams: any = {
            // Force fresh auth so an existing SSO session for a different user can't satisfy the invite (MOP-27516).
            prompt: 'login',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            login_hint: this.invitation['invitedEmailAddress'],
            tenantLabel: this.tenantLabel,
            nfToken: nfToken,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            redirect_uri: window.location.origin + '/callback',
        };

        if (useAcul) {
            // ACUL: account is created on accept -> go to the signup screen. Classic invite params
            // are ignored; ext-idps (space-separated) is ACUL's only IdP-filter channel.
            // eslint-disable-next-line @typescript-eslint/naming-convention
            authorizationParams.screen_hint = 'signup';
            authorizationParams['ext-idps'] = connectionIds.join(' ');
        } else {
            // Classic ULP: login.html renders the invitation "Last step" box from these params + branding.
            authorizationParams.isInvitation = 'true';
            authorizationParams.nfEmail = this.invitation['invitedEmailAddress'];
            authorizationParams.showSignup = hasUsernamePassword ? 'true' : 'false';
            // { connectionId, name } objects so each IdP button is labeled (bare ids show "undefined").
            authorizationParams.identityProviders = JSON.stringify(
                connectionIds.map((id) => ({ connectionId: id, name: id }))
            );

            await this.brandingService
                .fetchBrandCss(this.tenantLabel)
                .then((rawCss) => {
                    authorizationParams.branding = rawCss;
                })
                .catch(() => {
                    this.logger.info('no branding for organization: ' + this.tenantLabel);
                })
                .finally(() => {
                    authorizationParams.branding = (authorizationParams.branding || '') + this.brandingService.loginBrandingCSS;
                });
        }

        // target: '/callback' keeps the user on the spinner page while the invitation
        // acceptance completes in the background. auth0.service.ts navigates to the
        // dashboard after _setSession succeeds.
        await this.auth0Service.loginWithRedirect({
            appState: { inviteKey: this.key, nfToken, target: '/callback' },
            authorizationParams,
        });
    }

    decline() {
        const data = {
          title: 'Decline Invitation',
          appendId: 'DeclineInvitation',
          subtitle: `Are you sure you want to decline this invitation? Once declined it can no longer be accepted.`,
          icon: 'Cancel',
          action: 'Yes, continue',
          cancelAction: 'Oops, no get me out of here',
          isTimeoutWarning: true,
        };
        this.dialogRef = this.dialogForm.open(ConfirmComponent, {
          data: data,
          height: '340px',
          width: '600px',
          autoFocus: false,
        });
        this.dialogRef.afterClosed().subscribe((result) => {
          if (!result) {
            return;
          }
          this.iamService.declineInvite(this.key).subscribe(() => {
            this.screen = 'declined';
          });
        });
    }

    reviewInvitation() {
        this.logger.info('Reviewing the invite. Invitation this.key is: ' + this.key);

        this.iamService.getBy('invitations', 'key', this.key).subscribe(
            (res: any) => {
                this.logger.info('Invitation result', res);
                this.invitation = res;

                // @TODO Need a screen for an invitation already being used
                if (this.invitation) {
                    this.tenantLabel = this.invitation['toTenantLabel'];
                    const expireMoment = moment.unix(this.invitation.expiration.epochSecond);
                    this.expires = expireMoment.fromNow();

                    if (this.invitation['accepted']) {
                        this.screen = 'inuse';
                    } else if (this.invitation['state'] === 'Expired') {
                        this.screen = 'expired';
                    } else if (this.invitation['state'] === 'Revoked') {
                        this.screen = 'revoked';
                    } else if (this.invitation['accepted'] == null) {
                        this.screen = 'review';
                    } else {
                        this.screen = 'declined';
                    }
                } else {
                    this.screen = 'invalid';
                }
                localStorage.setItem('inviteKey', this.key);
            },
            () => {
                this.screen = 'invalid';
            }
        );
    }

    confirmInvitation(acceptToken, state) {
        this.logger.info('Confirming the invite...');
        const key = localStorage.getItem('inviteKey');

        this.logger.info('Invite Key Is: ' + key);
        this.logger.info('Accept Token Is: ' + acceptToken);

        this.iamService.confirmInvite(key, acceptToken).subscribe((res) => {
            this.logger.info('Confirm invite', res);

            // invite should be confirmed
            if (state) {
                this.key = localStorage.removeItem('inviteKey');
                this.auth.continue(state);
            } else {
                alert('Unable to resume authorization, please try a fresh login or have a new invitation sent');
            }
        });
    }
}
